import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, of } from 'rxjs';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LabelsModalComponent } from '@shared/components/action-bar/labels-modal/labels-modal.component';
import { ModalType } from '@shared/models/modal.model';
import { ExportLabelModalData } from '@store/labels/labels.model';
// @ts-ignore
import {
  FileLink,
  PropertyInformationExport,
  SearchField,
  SearchRequestTypes,
  SidePanelTab
} from '@store/search-board/search-board.model';
import { ActionName } from '@shared/models/effects-names.model';
import { ExportService } from '@root/search/services/export.service';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { select, Store } from '@ngrx/store';
import { selectMySearchFieldsData, selectQuickSearchFieldsData, selectSidePanelTab } from '@store/search-board/search-board.selector';
// @ts-ignore
import { DISPLAY_FIELDS, TOTAL_RECORDS } from '@root/search/models/display-fields.constant';

@Injectable()
export class LabelsEffects {
  constructor(private readonly actions$: Actions,
              private readonly dialog: MatDialog,
              private readonly exportService: ExportService,
              private readonly spinnerOverlayService: SpinnerOverlayService,
              private readonly store$: Store) {
  }

  startLabelExport$: Observable<SearchBoardActions.OpenLabelsModal | SearchBoardActions.GetGridExportInfo> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.START_LABEL_EXPORT),
        switchMap((action: SearchBoardActions.StartLabelsExport) => [
          new SearchBoardActions.OpenLabelsModal({
            checkedProperties: action.payload.checkedProperties,
            shouldUseCheckedPropertiesCount: action.payload.shouldUseCheckedPropertiesCount
          }),
          new SearchBoardActions.GetGridExportInfo()
        ])
      );
  });

  openLabelsModal$: Observable<SearchBoardActions.QuickSearch | SearchBoardActions.MySearch | SearchBoardActions.RunLabelsExport> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.OPEN_LABELS_MODAL),
        switchMap((action: SearchBoardActions.OpenLabelsModal) => {
          return this.dialog.open(LabelsModalComponent, {
            panelClass: ModalType.SMALL,
            data: {
              checkedPropertiesCount: action.payload.checkedProperties.length,
              shouldUseCheckedPropertiesCount: action.payload.shouldUseCheckedPropertiesCount
            }
          })
            .afterClosed()
            .pipe(
              filter(Boolean),
              map(([result, range]) => [result, range, action.payload])
            );
        }),
        withLatestFrom(
          this.store$.pipe(select(selectQuickSearchFieldsData)),
          this.store$.pipe(select(selectMySearchFieldsData)),
          this.store$.pipe(select(selectSidePanelTab))
        ),
        map(([[modalData, range, records], quickSearchFields, mySearchFields, selectedTab]: [[ExportLabelModalData, { from: number, to: number }, PropertyInformationExport], Array<SearchField>, Array<SearchField>, SidePanelTab]) => {
          const selected = records.checkedProperties.map((selectedRecord) => selectedRecord.propertyIdentifier);
          const exportProps = records.checkedProperties.map((prop) => {
            return prop.propertyIdentifier;
          })
          if (records.shouldUseCheckedPropertiesCount) {
            return new SearchBoardActions.RunLabelsExport({
              ...modalData,
              propertyIdentifiers: exportProps,
              remainingExports: modalData.allowedExportLimit - exportProps.length,
              recordsInReport: exportProps.length
            });
          }

          let propertyIdentifiers;

          if (modalData.printRangeSelected) {
            propertyIdentifiers = selected.slice(modalData.printRangeFrom - 1, modalData.printRangeTo);
          } else {
            if (selected.length > modalData.availableLimit) {
              propertyIdentifiers = selected.slice(0, modalData.availableLimit);
            } else {
              propertyIdentifiers = selected;
            }
          }

          if (selectedTab === SidePanelTab.quickSearch) {
            return new SearchBoardActions.QuickSearch({
              searchFields: quickSearchFields,
              displayFields: DISPLAY_FIELDS,
              totalRecords: TOTAL_RECORDS,
              searchRequestType: SearchRequestTypes.EXPORT_LABELS,
              exportResults: true,
              range,
              searchRequestData: {
                ...modalData,
                remainingExports: modalData.allowedExportLimit - propertyIdentifiers.length,
                recordsInReport: propertyIdentifiers.length
              }
            })
          } else {
            return new SearchBoardActions.MySearch({
              searchFields: mySearchFields,
              searchRequestType: SearchRequestTypes.EXPORT_LABELS,
              exportResults: true,
              range,
              searchRequestData: {
                ...modalData,
                remainingExports: modalData.allowedExportLimit - propertyIdentifiers.length,
                recordsInReport: propertyIdentifiers.length
              }
            });
          }
        })
      );
  });

  runExportLabels$: Observable<unknown> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.RUN_LABELS_EXPORT),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.RunLabelsExport) => {
          return this.exportService.generateExportLabelsLink(action.payload).pipe(
            tap((exportResponse: FileLink) => this.exportService.openLink(exportResponse.fileLink)),
            catchError(() => of(void (0)))
          );
        }, ActionName.RUN_LABEL_EXPORT)
      );
  }, {dispatch: false});

}
